import React from 'react'

import { Alert, Link } from '@atoms'
import Pagination from '@molecules/Pagination'
import LoadingWrapper from '@molecules/LoadingWrapper'
import SearchField, { searchFn } from '@molecules/SearchField'
import type { GroupsIndexQueryType } from '@frontend/graphql/queries/groups'

import GroupCard from '../features/list-groups/GroupIndexGroupCard'
import type { SharedInertiaProps } from '../types/groupTypes'

import { scopedTranslation } from '@utils/I18n'
import EmptyState from '@molecules/EmptyState'
import { PageLayout } from '@components/layouts/PageLayout'

const t = scopedTranslation('group_index')
const tShared = scopedTranslation('shared')

type GroupsIndexProps = {
  allGroups: GroupsIndexQueryType['allGroups']
  groups: GroupsIndexQueryType['groups']
} & SharedInertiaProps

function GroupsIndex({ allGroups, groups: groupsData, permissions }: GroupsIndexProps) {
  const [loading, setLoading] = React.useState(false)

  const { nodes: groups, totalCount: matchingGroupsCount, totalPages } = groupsData
  const allGroupsCount = allGroups.totalCount
  const partialReloadKeys = ['groups']
  const searchParamName = 'search'
  const searchValue = new URLSearchParams(window.location.search).get(searchParamName)

  return (
    <PageLayout.Root>
      <PageLayout.Header
        icon="community"
        heading={t('title')}
        subtitle={t('subtitle')}
        actions={
          permissions.canCreateGroup && (
            <Link
              href="/groups/new"
              as="inertiaLink"
              rank="primary"
              trailingIcon="plus-circle"
              dataCompId={'groups_add-new-group'}
            >
              {tShared('nav_links.add_new_group')}
            </Link>
          )
        }
        search={
          <div className="tw:max-w-xs tw:[&>div>label]:font-bold">
            <SearchField
              fieldName={searchParamName}
              displayName={t('search_groups')}
              inputWrapperClassName="tw:min-h-12 tw:border-gray-cool-200"
              onChange={(event) => {
                searchFn(event, partialReloadKeys, setLoading)
              }}
              dataCompId="groups_search-field"
            />
          </div>
        }
      />
      <PageLayout.Body>
        {allGroupsCount === 0 ? (
          <EmptyState headline={t('empty_state.headline')} subtext={t('empty_state.subtext')} iconType={'info-circle'}>
            {permissions.canCreateGroup && (
              <Link
                href="/groups/new"
                as="inertiaLink"
                size="xl"
                rank="primary"
                trailingIcon="plus-circle"
                dataCompId={'groups_add-new-group'}
                ariaLabel={t('empty_state.button')}
              >
                {t('empty_state.button')}
              </Link>
            )}
          </EmptyState>
        ) : (
          <LoadingWrapper loading={loading}>
            <div role="status" aria-busy={loading}>
              {searchValue && (
                <div className="tw:pb-4">
                  <Alert type="info" icon="search-lg">
                    {t('showing_x_groups', { count: matchingGroupsCount })}
                  </Alert>
                </div>
              )}
            </div>
            <div role="list" className="tw:flex tw:flex-col tw:gap-4">
              {groups?.map((group, index) => {
                if (!group) return null
                return (
                  <GroupCard
                    key={group?.id}
                    group={group}
                    permissions={permissions}
                    dataCompId={`groups-info-card-${index}`}
                  />
                )
              })}
            </div>
          </LoadingWrapper>
        )}
      </PageLayout.Body>
      <PageLayout.Footer>
        <div className="tw:flex tw:justify-center">
          <Pagination totalPages={totalPages ?? 1} only={partialReloadKeys} />
        </div>
      </PageLayout.Footer>
    </PageLayout.Root>
  )
}

export default GroupsIndex
